import React, { useState } from "react";
import {
    Box,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { Checkbox, Select, H4 } from '@tviklax/ui-components';
import { SellerRegistrationFormData } from "../../components";

interface Option {
    id: string | number;
    name: string;
}

interface AgentInformationFormProps {
    formData: SellerRegistrationFormData;
    handleChange: (e: any) => void;
    handleNext: () => void;
    loading: boolean;
    agentsLoading: boolean;
    agentsOptions: Option[];
    formId: string;
    setNextDisabled?: (disabled: boolean) => void;
}

const AgentInformationForm: React.FC<AgentInformationFormProps> = ({
    formData,
    handleChange,
    handleNext,
    loading,
    agentsLoading,
    agentsOptions,
    formId,
    setNextDisabled,
}) => {
    const [referredByAgent, setReferredByAgent] = useState(formData.referredByAgent || false);
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: "all",
        defaultValues: formData,
    });

    const btnDisabled = loading;

    React.useEffect(() => {
        setNextDisabled?.(btnDisabled);
    }, [btnDisabled, setNextDisabled]);

    const onSubmit = () => {
        if (btnDisabled) return;
        handleNext();
    };

    return (
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ pb: 1, pt: 1 }}>
                    <H4 style={{ fontWeight: 600 }}>Agent Information</H4>
                </Box>

                <Box sx={{ mt: 5 }}>
                    <Box>
                        <H4 style={{ fontSize: "18px", marginBottom: "0.5rem" }}>
                            Do you manage product uploads and order processing yourself? If
                            not, an agent can assist you in efficiently handling your
                            eCommerce store.
                        </H4>

                        <Controller
                            name="referredByAgent"
                            control={control}
                            defaultValue={false}
                            render={({ field: { value, onChange, ...fieldProps } }) => (
                                <Checkbox
                                    {...fieldProps}
                                    checked={value}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const isChecked = e.target.checked;
                                        onChange(isChecked);
                                        setReferredByAgent(isChecked);
                                        handleChange(e);
                                    }}
                                    label="Need assistance from an agent?"
                                />
                            )}
                        />
                    </Box>

                    {referredByAgent && (
                        <Box sx={{ mt: 2 }}>
                            <Controller
                                name="agentId"
                                control={control}
                                defaultValue={formData.agentId}
                                rules={{
                                    required: referredByAgent ? "Agent is required" : false,
                                }}
                                render={({ field: { onChange, value, ref } }) => (
                                    <Select
                                        ref={ref}
                                        label="Select an agent"
                                        name="agentId"
                                        value={value || ""}
                                        error={errors.agentId?.message as string}
                                        items={!agentsLoading ? (
                                            agentsOptions.length > 0 ? (
                                                agentsOptions.map((option) => ({
                                                    label: option.name,
                                                    value: option.id.toString(),
                                                }))
                                            ) : []
                                        ) : []}
                                        placeholder={agentsLoading ? "Loading..." : "Select an agent"}
                                        onChange={(e) => {
                                            handleChange(e);
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}
                </Box>

            </Box>
        </form>
    );
};

export default AgentInformationForm;
